// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Every fetch the site makes, in one place, with one error path. Nothing here throws a bare
// network error at a view — a failed load produces a message a reader can act on.

import type {
  Clock,
  CoverageRow,
  Daily,
  Episode,
  EpisodeSlice,
  ExceedanceRow,
  Labels,
  Meta,
  Rose,
  Station,
  StationYear,
} from './types';

const BASE = 'data';
const cache = new Map<string, Promise<unknown>>();

export class DataError extends Error {
  constructor(readonly resource: string, cause: unknown) {
    super(`Could not load ${resource}`);
    this.cause = cause;
  }
}

async function get<T>(path: string, optional = false): Promise<T> {
  const key = path;
  if (!cache.has(key)) {
    cache.set(
      key,
      (async () => {
        try {
          const response = await fetch(`${BASE}/${path}`);
          if (!response.ok) {
            if (optional && response.status === 404) return null;
            throw new Error(`HTTP ${response.status}`);
          }
          return await response.json();
        } catch (error) {
          cache.delete(key);
          if (optional) return null;
          throw new DataError(path, error);
        }
      })()
    );
  }
  return cache.get(key) as Promise<T>;
}

export const loadMeta = () => get<Meta>('meta.json');
export const loadStations = () => get<Station[]>('stations.json');
export const loadLorenz = () => get<StationYear[]>('lorenz.json');
export const loadEpisodes = () => get<Episode[]>('episodes.json');
export const loadExceedances = () => get<ExceedanceRow[]>('exceedances.json');
export const loadLabels = () => get<Labels>('labels.json');
export const loadCoverage = () => get<CoverageRow[]>('coverage.json');
export const loadEpisodeSlice = (id: string) => get<EpisodeSlice | null>(`episode/${id}.json`, true);
export const loadClock = (clusterId: string) => get<Clock | null>(`clock/${clusterId}.json`, true);
export const loadRose = (clusterId: string) => get<Rose | null>(`rose/${clusterId}.json`, true);
export const loadDaily = (clusterId: string) => get<Daily | null>(`daily/${clusterId}.json`, true);

export interface Bundle {
  meta: Meta;
  stations: Station[];
  episodes: Episode[];
  lorenz: StationYear[];
}

/** The four files every view needs. Loaded once at boot behind the progress bar. */
export async function loadBundle(): Promise<Bundle> {
  const [meta, stations, episodes, lorenz] = await Promise.all([
    loadMeta(),
    loadStations(),
    loadEpisodes(),
    loadLorenz(),
  ]);
  return { meta, stations, episodes, lorenz };
}
